import { supabase } from '../../lib/supabase'
import type { RecordState } from '../../components/agent/StatusChip'
import type { Category, DayEntry } from '../../components/agent/scheduleDay'
import type { PeriodDateSkeleton } from '../../lib/payPeriods'
import { SCHEDULE_ENTRY_COLUMNS, dayFor, type EntryRow } from '../supervisor/approvedScheduleStore'

/**
 * The signed-in agent's own schedule, read from the real tables: their
 * schedule_entries (row-level security lets an agent read their own,
 * schedule_entries_read_own), the state of their own submission for a
 * period, and their team's latest published version
 * (schedule_versions_read_own_team). An agent's employee record is the one
 * whose profile is theirs (employees_read_own); an account with none has no
 * schedule.
 */
async function myEmployeeId(userId: string): Promise<string | null> {
  const { data } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  return data?.id ?? null
}

// A day no schedule entry covers, so a view has something real to show and
// tap into instead of a hole. `unscheduled` is a UI-only category, not a
// status code; see scheduleDay.ts.
export function unscheduledDay(date: PeriodDateSkeleton): DayEntry {
  return { ...date, category: 'unscheduled', label: 'Nothing scheduled' }
}

// Every date given, in order: the agent's own entry where one covers it,
// an unscheduled day where none does.
export async function getMyScheduleDays(userId: string, dates: PeriodDateSkeleton[]): Promise<DayEntry[]> {
  const first = dates[0]?.date
  const last = dates[dates.length - 1]?.date
  const employeeId = await myEmployeeId(userId)
  if (!first || !last || !employeeId) return dates.map(unscheduledDay)

  const { data, error } = await supabase
    .from('schedule_entries')
    .select(SCHEDULE_ENTRY_COLUMNS)
    .eq('employee_id', employeeId)
    .gte('date', first)
    .lte('date', last)
  if (error || !data) return dates.map(unscheduledDay)

  const byDate = new Map((data as unknown as EntryRow[]).map((row) => [row.date, row]))
  return dates.map((date) => {
    const entry = byDate.get(date.date)
    const day = entry ? dayFor(entry) : null
    return day ? { ...date, ...day } : unscheduledDay(date)
  })
}

// Where the agent's own schedule for a period stands: their submission's
// state when they have submitted it, published once entries exist without
// one (a supervisor published a schedule for them), and nothing when there
// is no schedule at all.
export async function getMyPeriodState(userId: string, periodStart: string, days: DayEntry[]): Promise<RecordState | null> {
  const employeeId = await myEmployeeId(userId)
  if (employeeId) {
    const { data } = await supabase
      .from('schedule_submissions')
      .select('state')
      .eq('employee_id', employeeId)
      .eq('period_start', periodStart)
      .maybeSingle()
    if (data) return data.state as RecordState
  }
  return days.some((day) => day.category !== 'unscheduled') ? 'published' : null
}

export type TeamScheduleDay = { date: string; category: Category; label: string; detail?: string }
export type TeamSchedule = {
  teamName: string
  // Every employee the team's latest published version names for the
  // period, with each of their days. Empty until the team publishes.
  members: { id: string; name: string; days: TeamScheduleDay[] }[]
}

type VersionRow = {
  entries: { employeeId?: string; employeeName: string; date: string; category: Category; label: string; detail?: string }[]
}

type PlacementRow = { team_id: string; teams: { name: string } | null }

// The agent's own team as its supervisor last published it for a period:
// name, shift time, and work status for everyone on it, per
// docs/architecture/data-model.md's "published versions of their team". Null
// for an agent with no team placement today.
export async function getMyTeamSchedule(userId: string, periodStart: string): Promise<TeamSchedule | null> {
  const employeeId = await myEmployeeId(userId)
  if (!employeeId) return null

  const today = new Date()
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  const { data: placements } = await supabase
    .from('employee_assignments')
    .select('team_id, teams(name)')
    .eq('employee_id', employeeId)
    .eq('active', true)
    .lte('effective_from', iso)
    .or(`effective_to.is.null,effective_to.gte.${iso}`)
  const placement = ((placements ?? []) as unknown as PlacementRow[])[0]
  if (!placement?.teams) return null

  const { data: version } = await supabase
    .from('schedule_versions')
    .select('entries, version')
    .eq('team_id', placement.team_id)
    .eq('period_start', periodStart)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle()

  // Grouped by employee id, since a display name is not unique; a snapshot
  // published before ids were recorded has none and falls back to the name.
  const members = new Map<string, { name: string; days: TeamScheduleDay[] }>()
  for (const entry of ((version as unknown as VersionRow | null)?.entries ?? [])) {
    const id = entry.employeeId ?? entry.employeeName
    const member = members.get(id) ?? { name: entry.employeeName, days: [] }
    member.days.push({ date: entry.date, category: entry.category, label: entry.label, detail: entry.detail })
    members.set(id, member)
  }
  return {
    teamName: placement.teams.name,
    members: Array.from(members, ([id, member]) => ({ id, ...member })).sort((a, b) => a.name.localeCompare(b.name)),
  }
}
